import {
  AmbientLight,
  DirectionalLight,
  EquirectangularReflectionMapping,
  NeutralToneMapping,
  OrthographicCamera,
  PCFSoftShadowMap,
  PMREMGenerator,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
  type Texture,
} from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';

export interface PlayBounds {
  width: number;
  depth: number;
  centerX?: number;
  centerZ?: number;
  /** Override the landscape baseline elevation (degrees). Tall layouts can
   * push toward 60-70° so the back of the table doesn't crop. */
  baselineElevationDeg?: number;
}

export interface ThreeScene {
  scene: Scene;
  camera: OrthographicCamera;
  renderer: WebGLRenderer;
  controls: OrbitControls;
  loadHDRI(url: string): Promise<void>;
  resize(): void;
  setBounds(bounds: PlayBounds): void;
  dispose(): void;
}

export function createScene(canvas: HTMLCanvasElement): ThreeScene {
  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: false });
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = NeutralToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;
  renderer.shadowMap.needsUpdate = true;

  const scene = new Scene();

  // Orthographic projection — production mahjong solitaire titles
  // (Microsoft Mahjong, GNOME Mahjongg, KMahjongg, Mahjong Titans, Sparrow)
  // overwhelmingly use parallel projection for the play view. Perspective
  // foreshortens the back rows and makes lower-layer tiles visually shrink
  // behind upper-layer tiles, which destroys the per-layer offset cue.
  // With ortho every tile in the same layer is the same screen size, so
  // the only depth cue is the per-layer XY offset — exactly what we want
  // the eye to read.
  const camera = new OrthographicCamera(-1, 1, 1, -1, 0.05, 80);
  camera.position.set(0, 12, 9);
  camera.lookAt(0, 0, 0);

  const ambient = new AmbientLight(0xffffff, 0.4);
  scene.add(ambient);

  let hdriLoaded = false;

  const key = new DirectionalLight(0xfff0e0, 1.6);
  key.position.set(4, 11, 6);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.left = -8;
  key.shadow.camera.right = 8;
  key.shadow.camera.top = 6;
  key.shadow.camera.bottom = -6;
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 30;
  key.shadow.bias = -0.0003;
  key.shadow.radius = 2;
  scene.add(key);

  const controls = new OrbitControls(camera, canvas);
  controls.enablePan = false;
  controls.enableRotate = false;
  controls.enableZoom = false;
  controls.target.set(0, 0, 0);
  controls.update();

  const pmrem = new PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();

  async function loadHDRI(url: string) {
    const texture: Texture = await new Promise((resolve, reject) => {
      new RGBELoader().load(url, resolve, undefined, reject);
    });
    texture.mapping = EquirectangularReflectionMapping;
    const env = pmrem.fromEquirectangular(texture).texture;
    scene.environment = env;
    scene.environmentIntensity = 0.55;
    if (!hdriLoaded) {
      ambient.intensity = 0.08;
      key.intensity = 2.2;
      hdriLoaded = true;
    }
    texture.dispose();
  }

  let playBounds: PlayBounds = { width: 12, depth: 8, centerX: 0, centerZ: 0 };

  function setBounds(b: PlayBounds) {
    playBounds = { centerX: 0, centerZ: 0, ...b };
    resize();
  }

  function resize() {
    const dpr = Math.min(window.devicePixelRatio, 1.75);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    const aspect = w / h;

    const refAspect = 1.6;
    const portraitAspect = 0.55;
    const t = Math.max(0, Math.min(1, (refAspect - aspect) / (refAspect - portraitAspect)));
    const baseElev = playBounds.baselineElevationDeg ?? 60;
    const elevationDeg = baseElev + t * (86 - baseElev);
    const elev = (elevationDeg * Math.PI) / 180;

    // Ortho framing: choose the half-width of the visible world that fits
    // both the layout's X width AND its projected Z depth (Z foreshortens
    // by sin(elev) when viewed from above). PAD leaves a margin so head/
    // tail tiles aren't pressed against the canvas edge.
    const PAD = 1.18;
    const halfW = (playBounds.width * PAD) / 2;
    const halfD = (playBounds.depth * PAD * Math.sin(elev)) / 2;
    // Fit ortho frustum to whichever dimension is the binding constraint
    // for the current aspect ratio.
    const orthoHalfWByAspect = halfD * aspect;
    const orthoHalfW = Math.max(halfW, orthoHalfWByAspect);
    const orthoHalfH = orthoHalfW / aspect;

    const cx = playBounds.centerX ?? 0;
    const cz = playBounds.centerZ ?? 0;

    camera.left = -orthoHalfW;
    camera.right = orthoHalfW;
    camera.top = orthoHalfH;
    camera.bottom = -orthoHalfH;
    camera.near = 0.05;
    camera.far = 80;
    camera.updateProjectionMatrix();

    // Distance is irrelevant for ortho (no perspective scale), but the
    // camera still needs a real position so light direction and shadow
    // casting work. Pick a distance large enough to clear the stack.
    const dist = 24;
    camera.position.set(cx, dist * Math.sin(elev), cz + dist * Math.cos(elev));
    camera.lookAt(cx, 0, cz);
    controls.target.set(cx, 0, cz);
    controls.update();
  }

  function dispose() {
    controls.dispose();
    pmrem.dispose();
    renderer.dispose();
  }

  resize();
  window.addEventListener('resize', resize);

  return { scene, camera, renderer, controls, loadHDRI, resize, setBounds, dispose };
}
